import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { settingsApi } from '../services/api';
import {
  FIELD_OPERATORS,
  buildFilterableFields,
  applyTaskFilters,
  buildFilterPresets,
  presetFingerprint,
  presetToFilterGroups,
  conditionIsActive,
} from '../lib/taskFilterEngine';

// Shared task-filter state/engine for every view (List, Board, Gallery,
// Timeline, Table). Owns search + filter-group state, evaluates them against
// the task list, and persists the filter groups per view (table_config.<viewId>)
// so a view remembers its filters. Grouping/sorting/showCompleted stay in the
// individual views (Table).
//
// Usage: render <TaskFilterBar {...filters} /> above the view and pass
// `filters.filteredTasks` into whichever view is active.
export function useTaskFilters({
  tasks = [],
  viewId,                 // persistence key, e.g. `tasks-<viewId>`; null = no persistence
  currentUserId = null,
  customStatuses = [],
  statusConfig = {},
  priorityConfig = {},
  projects = [],
  labels = [],
  taskTypes = [],
  usersMap = {},
  teamsMap = {},
  customFieldDefs = [],
}) {
  const { t } = useTranslation();

  const [searchQuery, setSearchQuery] = useState('');
  const [filterGroups, setFilterGroups] = useState([]);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [showPresetMenu, setShowPresetMenu] = useState(false);
  const loadedRef = useRef(false);

  // ── Persistence: load filter groups for this view, then save on change ──
  useEffect(() => {
    if (!viewId) { loadedRef.current = true; return; }
    let cancelled = false;
    loadedRef.current = false;
    settingsApi.get()
      .then((res) => {
        if (cancelled) return;
        const saved = res.data?.table_config?.[viewId]?.filterGroups;
        if (Array.isArray(saved)) setFilterGroups(saved);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) loadedRef.current = true; });
    return () => { cancelled = true; };
  }, [viewId]);

  const saveTimer = useRef(null);
  useEffect(() => {
    if (!viewId || !loadedRef.current) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      settingsApi.update({ [`table_config.${viewId}.filterGroups`]: filterGroups }).catch(() => {});
    }, 700);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [filterGroups, viewId]);

  // ── Field model ──
  const filterableFields = useMemo(
    () => buildFilterableFields({ t, customStatuses, statusConfig, priorityConfig, projects, labels, taskTypes, usersMap, teamsMap, customFieldDefs }),
    [t, customStatuses, statusConfig, priorityConfig, projects, labels, taskTypes, usersMap, teamsMap, customFieldDefs],
  );

  const evalCtx = useMemo(
    () => ({ customStatuses, currentUserId, projects }),
    [customStatuses, currentUserId, projects],
  );

  // ── Filtered result ──
  const filteredTasks = useMemo(
    () => applyTaskFilters(tasks, { searchQuery, filterGroups }, evalCtx),
    [tasks, searchQuery, filterGroups, evalCtx],
  );

  // Apply the current search + filters to any task list (for views that render a
  // different subset than `tasks`, e.g. Home's curated table/agenda lists).
  const applyToTasks = useCallback(
    (list) => applyTaskFilters(list, { searchQuery, filterGroups }, evalCtx),
    [searchQuery, filterGroups, evalCtx],
  );

  // ── Condition/group CRUD ──
  const addGroup = useCallback(() => {
    if (filterableFields.length === 0) return;
    const first = filterableFields[0];
    const ops = FIELD_OPERATORS[first.type] ?? FIELD_OPERATORS.select;
    setFilterGroups(prev => [...prev, {
      id: crypto.randomUUID(),
      conditions: [{ id: crypto.randomUUID(), field: first.key, operator: ops[0], value: '' }],
    }]);
  }, [filterableFields]);

  const addOrToGroup = useCallback((groupId) => {
    if (filterableFields.length === 0) return;
    const first = filterableFields[0];
    const ops = FIELD_OPERATORS[first.type] ?? FIELD_OPERATORS.select;
    setFilterGroups(prev => prev.map(g =>
      g.id === groupId
        ? { ...g, conditions: [...g.conditions, { id: crypto.randomUUID(), field: first.key, operator: ops[0], value: '' }] }
        : g));
  }, [filterableFields]);

  const updateCondition = useCallback((groupId, condId, updated) => {
    setFilterGroups(prev => prev.map(g =>
      g.id === groupId ? { ...g, conditions: g.conditions.map(c => c.id === condId ? updated : c) } : g));
  }, []);

  const removeCondition = useCallback((groupId, condId) => {
    setFilterGroups(prev => prev
      .map(g => g.id === groupId ? { ...g, conditions: g.conditions.filter(c => c.id !== condId) } : g)
      .filter(g => g.conditions.length > 0));
  }, []);

  const removeGroup = useCallback((groupId) => {
    setFilterGroups(prev => prev.filter(g => g.id !== groupId));
  }, []);

  const clearFilters = useCallback(() => {
    setSearchQuery('');
    setFilterGroups([]);
  }, []);

  // ── Presets ──
  const presets = useMemo(() => buildFilterPresets(t), [t]);
  const activeFingerprint = presetFingerprint(filterGroups.map(g => g.conditions));
  const isPresetActive = useCallback(
    (preset) => presetFingerprint(preset.groups) === activeFingerprint,
    [activeFingerprint],
  );
  const applyPreset = useCallback((preset) => {
    if (presetFingerprint(preset.groups) === activeFingerprint) {
      setFilterGroups([]); // clicking the checked preset toggles it off
    } else {
      setFilterGroups(presetToFilterGroups(preset));
    }
    setShowPresetMenu(false);
  }, [activeFingerprint]);

  const hasActiveFilters = !!searchQuery || filterGroups.some(g => g.conditions.some(conditionIsActive));

  return {
    // result
    filteredTasks,
    applyToTasks,
    // search
    searchQuery, setSearchQuery,
    // groups
    filterGroups, filterableFields,
    addGroup, addOrToGroup, updateCondition, removeCondition, removeGroup, clearFilters,
    // panel + preset menu open state
    showFilterPanel, setShowFilterPanel,
    showPresetMenu, setShowPresetMenu,
    // presets
    presets, isPresetActive, applyPreset,
    // status
    hasActiveFilters,
  };
}
